import React from "react";
import {
  Button,
  Box,
  H2,
  Modal,
  ModalContent,
  ModalFooterButtons,
} from "@reside/ui";
import type {SsoConfirmationModalContent} from "./confirmationModalConstants";

/**
 * Confirmation modal shown before submitting SSO management changes.
 *
 * Displays a title, body question, Cancel button, and a primary confirm button.
 * Content is passed via the `content` prop—use {@link getSsoConfirmationModalContent}
 * from confirmationModalConstants to derive it from form state.
 *
 * Usage flow:
 * 1. User changes radio on SSO form → parent opens modal (isOpen=true)
 * 2. Parent passes content from getSsoConfirmationModalContent(pendingValues)
 * 3. User clicks Cancel → onRequestClose, parent closes modal
 * 4. User clicks confirm → onConfirm, parent submits and closes modal
 *
 * @see confirmationModalConstants - getSsoConfirmationModalContent, SsoConfirmationModalContent
 */
type Props = Readonly<{
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Called when user cancels or closes (e.g. backdrop click). Parent should set isOpen=false. */
  onRequestClose: () => void;
  /** Called when user confirms. Parent should submit form and set isOpen=false. */
  onConfirm: () => void;
  /** Title, body, and confirm button label. From getSsoConfirmationModalContent(values). */
  content: SsoConfirmationModalContent;
  /** Disables buttons and blocks closing while mutation is in progress */
  isLoading?: boolean;
}>;

export const SSOConfirmationModal = ({
  isOpen,
  onRequestClose,
  onConfirm,
  content,
  isLoading = false,
}: Props): JSX.Element => (
  <Modal
    isOpen={isOpen}
    onRequestClose={isLoading ? undefined : onRequestClose}
    noCloseButton
  >
    {({closeModal}) => (
      <>
        <ModalContent>
          <H2>{content.title}</H2>
          <Box marginTop={4}>{content.body}</Box>
        </ModalContent>
        <ModalFooterButtons>
          <Box
            width="100%"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Button
              outline
              color="primary"
              disabled={isLoading}
              onClick={closeModal}
              testId="ssoConfirmationModalCancel"
            >
              Cancel
            </Button>
            <Button
              color="danger"
              disabled={isLoading}
              onClick={onConfirm}
              testId="ssoConfirmationModalConfirm"
            >
              {isLoading ? "Saving..." : content.confirmButtonLabel}
            </Button>
          </Box>
        </ModalFooterButtons>
      </>
    )}
  </Modal>
);
